/**
 * Add / edit an audience.
 *
 * The count at the bottom recomputes on every change, so you can see the
 * segment shrink or grow as you narrow it rather than saving and guessing.
 */
import { useState, useMemo } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { AUDIENCE_FLAGS, MKT_CONTACT_STATUSES } from '../../constants/marketing.js';
import { audienceOf, reachable } from '../../domain/marketing.js';
import { salesTeam } from '../../domain/crm.js';
import Sheet, { Group, Field, OptionSelect } from '../Sheet.jsx';

export default function AudienceSheet({ id, onClose }) {
  const { S, crm, mkt, mktCtx, saveAudience, toast } = useApp();

  const existing = id ? mkt.audiences.find(a => a.id === id) : null;
  const [form, setForm] = useState(() => existing || {
    name: '', src: 'clients', flag: '',
    status: '', plan: '', type: '', owner: '', inactiveDays: ''
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const preview = useMemo(() => {
    const list = audienceOf(form, mktCtx);
    return {
      total: list.length,
      emailable: reachable(list, 'email', mktCtx).length,
      whatsapp: reachable(list, 'whatsapp', mktCtx).length,
      names: list.slice(0, 4).map(x => x.name)
    };
  }, [form, mktCtx]);

  // Every lifecycle vocabulary appears, since an audience can be built from
  // the client ledger, the contact list or marketing's own book.
  const statuses = form.src === 'marketing'
    ? MKT_CONTACT_STATUSES
    : [...new Set([...(S.statuses || []), ...(S.lStatus || [])])];

  const save = () => {
    if (!String(form.name).trim()) { toast('Name the audience'); return; }
    saveAudience(id, { ...form, name: String(form.name).trim() });
    onClose();
  };

  return (
    <Sheet
      icon="target"
      tone="g-purple"
      title={id ? 'Edit audience' : 'New audience'}
      onClose={onClose}
      onSave={save}
      saveLabel="Save audience"
    >
      <Group>Audience</Group>

      <Field label="Name" wide>
        <input autoFocus value={form.name || ''} onChange={e => set('name', e.target.value)} />
      </Field>

      <Field label="Built from">
        <select value={form.src} onChange={e => set('src', e.target.value)}>
          <option value="clients">Clients on the ledger</option>
          <option value="contacts">Leads and contacts</option>
          <option value="marketing">Marketing contacts</option>
        </select>
      </Field>

      {/*
        * The qualifying flags all read the ledger or the pipeline, and a
        * marketing contact has neither — it is a name and an address. That
        * source is narrowed by its subscription status alone, in the field
        * below, so this one would only offer filters that match nobody.
        */}
      {form.src === 'marketing' ? null : (
        <Field label="Who qualifies">
          <select value={form.flag || ''} onChange={e => set('flag', e.target.value)}>
            {AUDIENCE_FLAGS.map(([v, n]) => <option key={v || 'all'} value={v}>{n}</option>)}
          </select>
        </Field>
      )}

      <Field label="Status">
        <OptionSelect value={form.status} onChange={v => set('status', v)} options={statuses} blank="" />
      </Field>

      {/* Plan, type, owner and last-activity are all ledger and pipeline
          facts. A marketing contact has none of them, so offering the filters
          would only build a segment that matches nobody. */}
      {form.src === 'marketing' ? null : (
        <>
          <Field label="Plan">
            <OptionSelect value={form.plan} onChange={v => set('plan', v)} options={S.plans || []} blank="" />
          </Field>

          <Field label="Account type">
            <OptionSelect value={form.type} onChange={v => set('type', v)} options={S.types || []} blank="" />
          </Field>

          <Field label="Owner">
            <OptionSelect value={form.owner} onChange={v => set('owner', v)} options={salesTeam(S, crm, form.owner)} blank="" />
          </Field>

          <Field label="Quiet for at least">
            <input
              value={form.inactiveDays || ''}
              placeholder="days, optional"
              onChange={e => set('inactiveDays', e.target.value)}
            />
          </Field>
        </>
      )}

      <Field label="" wide>
        <div style={{ fontSize: 12.5, color: 'var(--ink2)' }}>
          <b>{preview.total}</b> qualify right now · {preview.emailable} emailable ·{' '}
          {preview.whatsapp} on WhatsApp
          {preview.names.length ? <><br />First few: {preview.names.join(', ')}</> : null}
        </div>
      </Field>
    </Sheet>
  );
}
